/**
 * THE WORKING MARK'S MARKUP — the one spinner, written as html or as a class name (Justin, 2026-09-26; design record
 * D-71, docs/PLAN-design-system-20260922.md §7a.10).
 *
 * Every spinner on prest0.ai is the logo's zero, turning: the ink arc and the satin-gold arc of the mark
 * (src/design/pavilion/markGeometry.ts), drawn by ONE stylesheet (src/styles/markSpinnerCss.ts) on this ONE element,
 * and turned on ONE clock (src/utils/markSpinnerPhase.ts), so a spinner rebuilt by a repaint carries on where the last
 * one was and every spinner on screen turns in step. This module writes the element and imports nothing: widgetHtml.ts
 * (on the first download) and the chat's html builders call it, and a browser gate bundles it.
 *
 * THE PROTOCOL LITERAL. `pendingSpin` in a message's html is how the chat recognises a live pill (turnIndicator.ts
 * `isActivePendingShapedMessage`, interruptedTurn.ts, useGlobalChat.ts, ConversationHistoryContext.tsx, …), and rows
 * stored before this change carry it too. So the element names its animation inline — `animation-name:pendingSpin`,
 * which the chat's sanitiser keeps along with `class` — and the stylesheet supplies the rest. Never drop it, never
 * rename the keyframes.
 *
 * DRAWN BY CLASS, NEVER BY SVG: the sanitiser strips svg, and an inline mask or position, from a message's html.
 */

/** The one class every spinner wears (the name the chat and its skin already knew). */
export const MARK_SPINNER_CLASS = 'pending-spinner';
/** The keyframes' name: a protocol string, read in stored html (see the header). */
export const MARK_SPIN_ANIMATION = 'pendingSpin';
/** One lap: the house's working beat (a working button's streak and the skeleton's sweep keep the same time). */
export const MARK_TURN_MS = 1800;
/**
 * Eased in and out, so every lap slows through the logo's own pose and the speed is zero at the seam (no joint in the
 * loop). Measured over 1.8 s: it rests within 10° of the logo for 29% of each lap (about half a second), and at its
 * fastest it turns 444°/s — never faster than the 0.8 s linear ring it replaces, which turned 450°/s all the time.
 * (A steeper curve lingers longer but races through the middle: `.6,0,.4,1` peaks at 500°/s, `.65,0,.35,1` at 571°/s.)
 */
export const MARK_TURN_EASING = 'cubic-bezier(.55,0,.45,1)';

/** The quiet ink (the owner's choice): the ink arc drawn at 40%, so the gold carries the motion. A CSS value. */
export const MARK_INK_OPACITY = '.4';

export type MarkSpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
/**
 * The five sizes, in px; the ring fills its box. Never larger than 24 (design system: "a spinner larger than 24px").
 * 10 a branch row, 12 a thread row, 16 a control or the pending pill, 18 a spinner standing on its own (the new
 * design's kit, elements.md), 24 the largest.
 */
export const MARK_SPINNER_SIZES_PX: Readonly<Record<MarkSpinnerSize, number>> = { xs: 10, sm: 12, md: 16, lg: 18, xl: 24 };

/**
 * - `mark`: the logo's two tones (the ink quiet, the gold carrying the motion);
 * - `mono`: both arcs in the colour of the words around it (a control's own ink: a button, a tooltip);
 * - `warn`: the gold arc in the warning ink (a retry that is waiting).
 */
export type MarkSpinnerTone = 'mark' | 'mono' | 'warn';

/**
 * The mark's inks, as the four properties the stylesheet reads. A spinner takes them from the nearest element that
 * sets them, so a surface sets them once for every spinner inside it.
 *
 * - ON LIGHT is the default (the stylesheet's own fallbacks are these values): the ink role, and the satin's deep end
 *   so the moving arc clears 3:1 against the lightest ground it turns over. A surface that keeps its own light ground
 *   whatever the chat's light (a card that stays white at dusk) sets it, so the new design's dusk inks never reach it.
 * - ON DARK is the logo's night inks: the cream ink and the logo's own satin. A surface that is dark in every light
 *   (today's thread rail) sets it.
 *
 * The new design's chat skin sets its own, per light, in its roles (pavilionChatCss.ts).
 */
export const MARK_INKS_ON_LIGHT = {
  '--mark-spin-ink': 'var(--color-ink)',
  '--mark-spin-gold-hi': 'var(--color-gold-lo)',
  '--mark-spin-gold-body': 'var(--color-gold-edge)',
  '--mark-spin-gold-lo': 'var(--color-gold-edge)',
} as const;
export const MARK_INKS_ON_DARK = {
  '--mark-spin-ink': 'var(--color-canvas)',
  '--mark-spin-gold-hi': 'var(--color-gold-sky)',
  '--mark-spin-gold-body': 'var(--color-accent)',
  '--mark-spin-gold-lo': 'var(--color-gold-lo)',
} as const;
/**
 * ON TINT: a light ground already warm with gold (the expenses list's beige loading line). The satin's lit top would sit
 * too close to such a ground to read (2.98:1 there, measured), so the arc takes the satin's deep end all the way down.
 */
export const MARK_INKS_ON_TINT = {
  '--mark-spin-ink': 'var(--color-ink)',
  '--mark-spin-gold-hi': 'var(--color-gold-edge)',
  '--mark-spin-gold-body': 'var(--color-gold-edge)',
  '--mark-spin-gold-lo': 'var(--color-gold-edge)',
} as const;
export type MarkInks = typeof MARK_INKS_ON_LIGHT | typeof MARK_INKS_ON_DARK | typeof MARK_INKS_ON_TINT;

/** A set of inks as CSS declarations, for a styled component that sets them on its surface. */
export function markInksCss(inks: MarkInks): string {
  return Object.entries(inks).map(([name, value]) => `${name}: ${value};`).join(' ');
}

export interface MarkSpinnerOptions {
  size?: MarkSpinnerSize;
  tone?: MarkSpinnerTone;
  /** Extra class names the caller already relies on (e.g. `pending-live-turn-spinner`). Code-authored only. */
  className?: string;
}

/**
 * The class list: the one class, then the caller's own, then the size and tone. The caller's classes sit right after
 * the one class so a pair a test or a stylesheet already reads (`pending-spinner pending-live-turn-spinner`) stays
 * whole whatever size or tone is asked for.
 */
export function markSpinnerClassName({ size = 'md', tone = 'mark', className }: MarkSpinnerOptions = {}): string {
  const parts = [MARK_SPINNER_CLASS];
  if (className) parts.push(className);
  if (size !== 'md') parts.push(`is-${size}`);
  if (tone !== 'mark') parts.push(`is-${tone}`);
  return parts.join(' ');
}

/**
 * The element, as html. A `div` where the spinner leads a row whose first `<span>` is its words (the pill), a `span`
 * inside a line of text (a card's head, a branch row).
 *
 * FOR A PENDING WIDGET ONLY. The `pendingSpin` it writes is the protocol word: a message whose html carries it is read
 * as a live pill — drawn without its bubble and pulled into the lane. A spinner anywhere else is `<MarkSpinner>` in
 * React (MarkSpinner.tsx); tests/workingMark/markup.test.ts reads this function's callers from the tree.
 */
export function markSpinnerHtml(options: MarkSpinnerOptions & { tag?: 'span' | 'div' } = {}): string {
  const tag = options.tag === 'div' ? 'div' : 'span';
  return `<${tag} class="${markSpinnerClassName(options)}" aria-hidden="true" style="animation-name:${MARK_SPIN_ANIMATION}"></${tag}>`;
}
